/**
 * Performance monitoring utility for API calls
 * Helps identify slow backend endpoints and bottlenecks
 */

interface ApiTiming {
  url: string;
  method: string;
  duration: number;
  timestamp: number;
  status?: number;
  error?: boolean;
}

class PerformanceMonitor {
  private timings: ApiTiming[] = [];
  private readonly maxEntries = 100; // Keep last 100 API calls
  private reportTimer: NodeJS.Timeout | null = null;

  /**
   * Start timing an API request
   */
  startTiming(url: string, method: string = 'GET'): () => void {
    const startTime = performance.now();
    const timestamp = Date.now();

    return (status?: number, error?: boolean) => {
      const duration = performance.now() - startTime;
      
      this.addTiming({
        url,
        method,
        duration,
        timestamp,
        status,
        error
      });

      // Log slow requests in development
      if (import.meta.env.DEV && duration > 1000) {
        console.warn(`🐌 Slow API call: ${method} ${url} took ${duration.toFixed(2)}ms`);
      }
    };
  }

  /**
   * Add a timing entry
   */
  private addTiming(timing: ApiTiming) {
    this.timings.push(timing);
    
    // Keep only the most recent entries
    if (this.timings.length > this.maxEntries) {
      this.timings = this.timings.slice(-this.maxEntries);
    }

    // Auto-print report after initial page load (after 10 API calls)
    if (import.meta.env.DEV && this.timings.length === 10) {
      console.log('📊 Initial page load complete - API Performance Report:');
      this.printReport();
    }

    // Schedule report for slow requests immediately
    if (timing.duration > 2000) {
      console.warn(`🚨 Very slow API call detected: ${timing.method} ${timing.url} took ${timing.duration.toFixed(2)}ms`);
    }
  }

  /**
   * Get performance statistics
   */
  getStats() {
    if (this.timings.length === 0) {
      return null;
    }

    const durations = this.timings.map(t => t.duration);
    const slowRequests = this.timings.filter(t => t.duration > 1000);
    const errorRequests = this.timings.filter(t => t.error);

    // Group by endpoint
    const byEndpoint: Record<string, ApiTiming[]> = {};
    this.timings.forEach(timing => {
      const endpoint = timing.url.split('?')[0]; // Remove query params
      if (!byEndpoint[endpoint]) {
        byEndpoint[endpoint] = [];
      }
      byEndpoint[endpoint].push(timing);
    });

    // Calculate stats per endpoint
    const endpointStats = Object.entries(byEndpoint).map(([endpoint, timings]) => {
      const endpointDurations = timings.map(t => t.duration);
      const avgDuration = endpointDurations.reduce((a, b) => a + b, 0) / endpointDurations.length;
      const maxDuration = Math.max(...endpointDurations);
      const minDuration = Math.min(...endpointDurations);
      
      return {
        endpoint,
        calls: timings.length,
        avgDuration: Math.round(avgDuration),
        maxDuration: Math.round(maxDuration),
        minDuration: Math.round(minDuration),
        slowCalls: timings.filter(t => t.duration > 1000).length,
        errorCalls: timings.filter(t => t.error).length,
      };
    }).sort((a, b) => b.avgDuration - a.avgDuration);

    return {
      totalRequests: this.timings.length,
      avgDuration: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
      maxDuration: Math.round(Math.max(...durations)),
      minDuration: Math.round(Math.min(...durations)),
      slowRequests: slowRequests.length,
      errorRequests: errorRequests.length,
      endpointStats,
      recentTimings: this.timings.slice(-10), // Last 10 requests
    };
  }

  /**
   * Print performance report to console
   */
  printReport() {
    const stats = this.getStats();
    if (!stats) {
      console.log('📊 No API performance data available');
      return;
    }

    console.group('📊 API Performance Report');
    console.log(`📈 Total Requests: ${stats.totalRequests}`);
    console.log(`⏱️  Average Duration: ${stats.avgDuration}ms`);
    console.log(`🐌 Slow Requests (>1s): ${stats.slowRequests}`);
    console.log(`❌ Error Requests: ${stats.errorRequests}`);
    
    if (stats.endpointStats.length > 0) {
      console.group('🎯 Slowest Endpoints:');
      stats.endpointStats.slice(0, 5).forEach(endpoint => {
        console.log(`${endpoint.endpoint}: ${endpoint.avgDuration}ms avg (${endpoint.calls} calls, ${endpoint.slowCalls} slow)`);
      });
      console.groupEnd();
    }

    if (stats.recentTimings.length > 0) {
      console.group('🕐 Recent API Calls:');
      stats.recentTimings.forEach(timing => {
        const status = timing.error ? '❌' : timing.duration > 1000 ? '🐌' : '✅';
        console.log(`${status} ${timing.method} ${timing.url}: ${Math.round(timing.duration)}ms`);
      });
      console.groupEnd();
    }
    
    console.groupEnd();
  }

  /**
   * Clear all timing data
   */
  clear() {
    this.timings = [];
  }
}

// Export singleton instance
export const apiPerformanceMonitor = new PerformanceMonitor();

// Add to window for debugging
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).apiPerformanceMonitor = apiPerformanceMonitor;
}